"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkerHandler = exports.CHECKER_STAGE_MAX = exports.CHECKER_STAGE_MIN = exports.CHECKER_CONTRACT_VALUE_MAX = exports.CHECKER_CONTRACT_VALUE_MIN = exports.CHECKER_COPY_VERSION = void 0;
exports.validateCheckerInput = validateCheckerInput;
const checker_copy_js_1 = require("./checker-copy.js");
Object.defineProperty(exports, "CHECKER_COPY_VERSION", { enumerable: true, get: function () { return checker_copy_js_1.CHECKER_COPY_VERSION; } });
const money_split_js_1 = require("./money-split.js");
exports.CHECKER_CONTRACT_VALUE_MIN = 1_000;
exports.CHECKER_CONTRACT_VALUE_MAX = 1_000_000;
exports.CHECKER_STAGE_MIN = 2;
exports.CHECKER_STAGE_MAX = 8;
const PCT_SUM_TARGET = 100;
/** Allow tiny float noise when homeowners enter decimals that still total 100. */
const PCT_SUM_EPSILON = 1e-9;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function error(field, code) {
    return { field, code };
}
function parseStage(raw, index) {
    if (!isRecord(raw)) {
        return { ok: false, errors: [error(`stages[${index}]`, "invalid_stage")] };
    }
    const key = typeof raw.key === "string" ? raw.key.trim() : "";
    const label = typeof raw.label === "string" ? raw.label.trim() : "";
    if (!key) {
        return { ok: false, errors: [error(`stages[${index}].key`, "required")] };
    }
    if (!label) {
        return { ok: false, errors: [error(`stages[${index}].label`, "required")] };
    }
    // Reject blank/null before Number() — Number(null) and Number("") are 0.
    // An explicitly entered 0 (number or "0") remains valid.
    const rawPct = raw.pct;
    if (rawPct === null || rawPct === undefined) {
        return { ok: false, errors: [error(`stages[${index}].pct`, "required")] };
    }
    let pct;
    if (typeof rawPct === "number") {
        pct = rawPct;
    }
    else if (typeof rawPct === "string") {
        const trimmed = rawPct.trim();
        if (trimmed === "") {
            return { ok: false, errors: [error(`stages[${index}].pct`, "required")] };
        }
        pct = Number(trimmed);
    }
    else {
        return { ok: false, errors: [error(`stages[${index}].pct`, "invalid_pct")] };
    }
    if (!Number.isFinite(pct)) {
        return { ok: false, errors: [error(`stages[${index}].pct`, "invalid_pct")] };
    }
    if (pct < 0) {
        return { ok: false, errors: [error(`stages[${index}].pct`, "negative_pct")] };
    }
    return { ok: true, value: { key, label, pct } };
}
function validateCheckerInput(input) {
    if (!isRecord(input)) {
        return { ok: false, errors: [error("input", "invalid")] };
    }
    const errors = [];
    const contractValue = Number(input.contractValue);
    if (!Number.isFinite(contractValue) || !Number.isInteger(contractValue)) {
        errors.push(error("contractValue", "invalid_contract_value"));
    }
    else if (contractValue < exports.CHECKER_CONTRACT_VALUE_MIN || contractValue > exports.CHECKER_CONTRACT_VALUE_MAX) {
        errors.push(error("contractValue", "contract_value_out_of_range"));
    }
    if (!Array.isArray(input.stages)) {
        errors.push(error("stages", "required"));
        return { ok: false, errors };
    }
    if (input.stages.length < exports.CHECKER_STAGE_MIN || input.stages.length > exports.CHECKER_STAGE_MAX) {
        errors.push(error("stages", "stage_count"));
    }
    const stages = [];
    for (let i = 0; i < input.stages.length; i++) {
        const parsed = parseStage(input.stages[i], i);
        if (parsed.ok === false) {
            errors.push(...parsed.errors);
        }
        else {
            stages.push(parsed.value);
        }
    }
    if (errors.length === 0) {
        const pctSum = stages.reduce((sum, stage) => sum + stage.pct, 0);
        if (Math.abs(pctSum - PCT_SUM_TARGET) > PCT_SUM_EPSILON) {
            errors.push(error("stages", "total_not_100"));
        }
    }
    if (errors.length > 0) {
        return { ok: false, errors };
    }
    return { ok: true, value: { contractValue, stages } };
}
function toSplitStages(stages) {
    return stages.map((stage, index) => ({
        pct: stage.pct,
        role: index === 0 ? "deposit" : "later",
    }));
}
function buildBreakdown(input) {
    const split = (0, money_split_js_1.splitSchedule)(input.contractValue, toSplitStages(input.stages));
    let paidByThisStage = 0;
    return input.stages.map((stage, index) => {
        const amount = split.stageAmounts[index] ?? 0;
        paidByThisStage += amount;
        return {
            label: stage.label,
            pct: stage.pct,
            amount,
            paidByThisStage,
            remainder: input.contractValue - paidByThisStage,
        };
    });
}
function evaluateChecks(stages) {
    const depositPct = stages[0]?.pct ?? 0;
    const handoverPct = stages[stages.length - 1]?.pct ?? 0;
    const nonZeroPcts = stages.map((stage) => stage.pct).filter((pct) => pct > 0);
    const skip2And6 = depositPct > 50;
    const flagged = [];
    const passed = new Set();
    // 1 — deposit > 20%
    if (depositPct > 20) {
        flagged.push(1);
    }
    else {
        passed.add(1);
    }
    // 2 — any non-zero payment > 50% (0% ignored); skipped when deposit > 50%
    if (skip2And6) {
        // skipped — neither flag nor pass
    }
    else if (nonZeroPcts.some((pct) => pct > 50)) {
        flagged.push(2);
    }
    else {
        passed.add(2);
    }
    // 3 — handover missing (0%); 4 — handover low (>0 and <5%)
    if (handoverPct === 0) {
        flagged.push(3);
    }
    else if (handoverPct < 5) {
        flagged.push(4);
    }
    else {
        passed.add(3);
        passed.add(4);
    }
    // 5 — fewer than 4 payments above 0%
    if (nonZeroPcts.length < 4) {
        flagged.push(5);
    }
    else {
        passed.add(5);
    }
    // 6 — first 2 non-zero payments sum > 50%; skipped when deposit > 50%.
    // Apply the sum to whatever non-zero payments exist (1 or 2) so a lone 100%
    // stage is not marked as "first 2 payments are 50% or less".
    if (skip2And6) {
        // skipped
    }
    else {
        const firstTwo = nonZeroPcts.slice(0, 2);
        const firstTwoSum = firstTwo.reduce((sum, pct) => sum + pct, 0);
        if (firstTwoSum > 50) {
            flagged.push(6);
        }
        else {
            passed.add(6);
        }
    }
    return { flagged, passed };
}
function buildFindings(flagged) {
    return flagged.map((id) => {
        const copy = checker_copy_js_1.CHECKER_FINDING_COPY[id];
        return {
            title: copy.title,
            whyItMatters: copy.whyItMatters,
            recommendation: copy.recommendation,
        };
    });
}
function buildPasses(passed) {
    const lines = [];
    for (const entry of checker_copy_js_1.CHECKER_PASS_COPY) {
        if (entry.checkIds.every((id) => passed.has(id))) {
            lines.push(entry.line);
        }
        if (lines.length >= 5) {
            break;
        }
    }
    return lines;
}
function buildHandshake(input) {
    const split = (0, money_split_js_1.splitSchedule)(input.contractValue, toSplitStages(input.stages));
    const depositProtectedInFull = split.stages[0]?.protectedAmount ?? 0;
    let secondStage;
    for (let i = 1; i < input.stages.length; i++) {
        if ((split.stageAmounts[i] ?? 0) > 0) {
            const stageSplit = split.stages[i];
            secondStage = {
                name: input.stages[i].label,
                protectedAmount: stageSplit.protectedAmount,
                toIdAmount: stageSplit.toIdAmount,
            };
            break;
        }
    }
    return {
        protectedTotal: split.protectedTotal,
        protectedRemaining: split.protectedRemaining,
        depositProtectedInFull,
        ...(secondStage ? { secondStage } : {}),
    };
}
function analyse(input) {
    const { flagged, passed } = evaluateChecks(input.stages);
    return {
        breakdown: buildBreakdown(input),
        flagged,
        passed,
        findings: buildFindings(flagged),
        passes: buildPasses(passed),
        handshake: buildHandshake(input),
    };
}
function formatMoney(amount) {
    return `S$${amount.toLocaleString("en-SG")}`;
}
function summariseChecker(result) {
    const riskWord = result.riskCount === 1 ? "potential risk" : "potential risks";
    const headline = result.riskCount === 0
        ? `Payment schedule passes all checks (${formatMoney(result.breakdown[0] ? result.handshake.protectedTotal : 0)} protectable)`
        : `${result.riskCount} ${riskWord} found`;
    const contractValue = result.breakdown.length
        ? result.breakdown[result.breakdown.length - 1]?.paidByThisStage ?? 0
        : 0;
    return {
        headline,
        summaryText: result.riskCount === 0
            ? `Schedule for ${formatMoney(contractValue)} passes all 6 checks.`
            : `Schedule for ${formatMoney(contractValue)}: ${result.riskTitles.join("; ")}.`,
        toolDataText: [
            `copy_version=${checker_copy_js_1.CHECKER_COPY_VERSION}`,
            `risks=${result.riskCount}`,
            `protectedTotal=${result.handshake.protectedTotal}`,
            `protectedRemaining=${result.handshake.protectedRemaining}`,
            `stages=${result.breakdown.map((row) => `${row.label}:${row.pct}%`).join("|")}`,
        ].join("; "),
    };
}
exports.checkerHandler = {
    validate: validateCheckerInput,
    preview(input) {
        const { breakdown, findings } = analyse(input);
        return {
            toolKey: "payment_schedule_checker",
            riskCount: findings.length,
            riskTitles: findings.map((finding) => finding.title),
            breakdown,
        };
    },
    run(input) {
        const { breakdown, findings, passes, handshake } = analyse(input);
        return {
            toolKey: "payment_schedule_checker",
            riskCount: findings.length,
            riskTitles: findings.map((finding) => finding.title),
            breakdown,
            findings,
            passes,
            contractChecklist: [...checker_copy_js_1.CHECKER_CONTRACT_CHECKLIST],
            handshake,
        };
    },
    summarise: summariseChecker,
};
